import { Add, Edit, ExpandMore, Star } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  colors,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { v4 } from 'uuid'
import useAlert from '../../../hooks/useAlert'
import AccordionHeader from '../AccordionHeader'
import EditLocationModal from './EditLocationModal'
import { blue } from '@mui/material/colors'
import Tooltip from '../../../common/Tooltip'
import ListEditor from '../../../common/ListEditor'

const CustomerLocations = ({
  customerLocations,
  saveCustomerLocations,
  setCustomerLocations,
  reset,
  expandedAccordions,
  setExpandedAccordions,
  renderedSaveButton,
  deleteLocation,
}) => {
  const expanded = expandedAccordions.locations
  const setExpanded = (val) =>
    setExpandedAccordions((prev) => ({ ...prev, locations: val }))

  const [isEditing, setIsEditing] = useState(true)
  const [currentLocationId, setCurrentLocationId] = useState(
    customerLocations[0]?.id
  )
  const [iframeLoading, setIframeLoading] = useState(true)

  const [isEditingLocation, setIsEditingLocation] = useState(false)
  const [isAddingLocation, setIsAddingLocation] = useState(false)

  const { createAlert, createGenericErrorAlert } = useAlert()

  const changeLocation = (id) => {
    if (id === currentLocationId) return
    setIframeLoading(true)
    setCurrentLocationId(id)
  }

  const currentLocation = customerLocations.find(
    (l) => l.id === currentLocationId
  )

  const getAddressQuery = (cl) => {
    if (!cl) return ''

    return [
      cl.streetAddress,
      cl.city,
      cl.stateOrProvince,
      cl.zipCode,
      cl.country,
    ]
      .filter((part) => !!part)
      .join(', ')
  }

  const handleCloseModal = () => {
    setIsEditingLocation(false)
    setIsAddingLocation(false)
  }

  const onSaveLocation = (newLocation) => {
    const ids = customerLocations.map((l) => l.id)

    const index = ids.indexOf(newLocation.id)
    let newArr = [...customerLocations]

    if (newLocation.isDefault) {
      newArr = newArr.map((c) => ({ ...c, isDefault: false }))
    }

    if (index === -1) {
      const newId = v4()
      changeLocation(newId)
      handleCloseModal()
      // setIsEditing(true)
      return setCustomerLocations(
        newArr.concat([{ ...newLocation, id: newId, isNew: true }])
      )
    }

    newArr.splice(index, 1, newLocation)
    handleCloseModal()
    setCustomerLocations(newArr)

    // return setIsEditing(true)
  }

  return (
    <>
      {(isEditingLocation || isAddingLocation) && (
        <EditLocationModal
          key={currentLocation?.id}
          open={isEditingLocation || isAddingLocation}
          location={currentLocation}
          onSave={onSaveLocation}
          onCancel={() => {
            handleCloseModal()
          }}
          isNew={isAddingLocation}
          isFirst={customerLocations.length === 0}
          deleteLocation={deleteLocation}
        />
      )}
      <Accordion
        elevation={8}
        sx={{ width: '100%', minWidth: '340px', maxWidth: '1250px' }}
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <b>Locations</b>
            <Tooltip
              textProps={{ sx: { whiteSpace: 'pre-line' } }}
              infoText={`In this section the system will automatically create a map of the primary business, based on the address in the account profile.

                  Additional map locations can be added.
                  
                  A description of the “Location” can be created for each map.`}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionHeader
            additionalButtons={[
              {
                label: 'Add',
                endIcon: <Add />,
                onClick: () => {
                  // setIsEditing(true)
                  setIsAddingLocation(true)
                },
              },
            ]}
            onSave={() =>
              saveCustomerLocations()
                .then(() => {
                  createAlert({ message: 'Locations successfully saved' })
                  setIsEditing(false)
                })
                .catch((e) => {
                  createGenericErrorAlert()
                })
            }
            hideEdit={!customerLocations.length || !isEditing}
            onCancel={reset}
            isEditing={isEditing}
            setIsEditing={(bool) => {
              setIsEditingLocation(bool)
              // setIsEditing(bool)
            }}
          />
          {customerLocations.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                mt: 2,
                flexWrap: 'wrap',
                ml: -3,
              }}
            >
              <Box sx={{ width: '50%', minWidth: '300px', ml: 3, mb: 1 }}>
                <ListEditor
                  list={customerLocations}
                  onChange={setCustomerLocations}
                  dragOnly
                  canReorder
                  displayOverride={(cl, i) => {
                    const isCurrentLocation = currentLocationId === cl.id

                    return (
                      <ListItem
                        onClick={() => changeLocation(cl.id)}
                        key={i}
                        sx={{
                          minHeight: '64px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          py: 1,
                          backgroundColor: isCurrentLocation
                            ? colors.blue['50']
                            : null,

                          '&:hover': {
                            backgroundColor: isCurrentLocation
                              ? colors.blue['100']
                              : colors.grey['200'],
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {cl.isDefault && (
                            <Star
                              fontSize='small'
                              sx={{ mr: 1, color: blue[800] }}
                            />
                          )}
                          {cl.name && (
                            <Typography sx={{ mr: 2 }}>
                              <b>{cl.name}</b>
                            </Typography>
                          )}
                          <Typography>{getAddressQuery(cl)}</Typography>
                        </Box>
                        {isCurrentLocation && (
                          <IconButton
                            sx={{ ml: 2 }}
                            onClick={(e) => {
                              e.stopPropagation()
                              changeLocation(cl.id)
                              setIsEditingLocation(true)
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </ListItem>
                    )
                  }}
                ></ListEditor>
              </Box>

              {currentLocation && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mx: 3,
                    position: 'relative',
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 'fit-content',
                      overflow: 'hidden',
                      paddingBottom: '56.25%',
                      minWidth: '300px',
                      flexGrow: 1,

                      height: 0,
                      ' iframe': {
                        left: 0,

                        top: 0,

                        height: '100%',

                        width: '100%',

                        position: 'absolute',
                      },
                    }}
                  >
                    <iframe
                      key={currentLocation.id}
                      width='600'
                      height='450'
                      style={{
                        border: '0',
                        backgroundColor: colors.grey[100],
                        zIndex: 1,
                      }}
                      loading='lazy'
                      src={`https://www.google.com/maps/embed/v1/search?q=${encodeURIComponent(
                        getAddressQuery(currentLocation)
                      )}&key=${process.env.SUP_MAPS_API_KEY}`}
                      onLoad={() => setIframeLoading(false)}
                    ></iframe>
                    {iframeLoading && (
                      <CircularProgress
                        sx={{
                          zIndex: 0,
                          position: 'absolute',
                          top: 'calc(50% - 20px)',
                          left: 'calc(50% - 20px)',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mr: -2 }}
          >
            {renderedSaveButton && renderedSaveButton}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default CustomerLocations
